import { Link } from 'wouter';
import type { RoundParticipant } from './types.js';

/** A participant that names who at the fund did the round. */
export type AttributedParticipant = RoundParticipant & {
  investorId: string;
  personName: string;
  personFundId: string;
};

export function attributedPartner(participant: RoundParticipant): AttributedParticipant | null {
  const { investorId, personName, personFundId } = participant;
  if (investorId === null || personName === null || personFundId === null) return null;
  return { ...participant, investorId, personName, personFundId };
}

/** Where a person is read: their fund's pane, opened at the People section on them. */
export function personHref(partner: { investorId: string; personFundId: string }): string {
  return `/?fund=${partner.personFundId}&person=${partner.investorId}`;
}

/**
 * The partner's name as a link to them. A name the press release gave carries
 * the sentence it came from and how sure the reading was, on hover: a person
 * deciding whether to call them should be able to see why we think it was them.
 */
export function PartnerLink({ partner }: { partner: AttributedParticipant }): React.JSX.Element {
  const press = partner.attribution === 'press';
  const confidence =
    partner.attributionConfidence === null
      ? null
      : `confidence ${Math.round(partner.attributionConfidence * 100)}%`;

  return (
    <span className="partner">
      <Link href={personHref(partner)} className="partner-link">
        {partner.personName}
      </Link>
      {press && (
        <span className="partner-tip" role="tooltip">
          {partner.attributionQuote !== null && (
            <span className="partner-quote">{partner.attributionQuote}</span>
          )}
          <span className="partner-source">
            {['from the press release', ...(confidence === null ? [] : [confidence])].join(' · ')}
          </span>
        </span>
      )}
    </span>
  );
}
